import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import PbxContext from '../../context/PbxContext';
import { fetchEndpointRegistrations } from '../../services';
import { formatAddress, formatClock, formatExpiry } from './registrationFormat';
import './EndpointRegistrationModal.css';

export const REGISTRATION_EVENT_DEBOUNCE_MS = 1000;
export const REGISTRATION_POLL_INTERVAL_MS = 30000;
const COUNTDOWN_TICK_MS = 1000;

const MESSAGES = {
  forbidden: 'Sem permissão para ver dados de registro.',
  notFound: 'Ramal não encontrado.',
  unavailable: 'Não foi possível consultar o registro agora.',
  noRegistration: 'Sem registro',
  unknownSource: 'Não identificado',
  unknownSourceHint: 'Ramal sem reescrita de Contact (NAT); o Asterisk não guarda o IP de chegada',
  noUserAgent: 'Não informado',
  truncated: 'Exibindo os 100 primeiros registros',
};

const errorMessageFor = (status) => {
  if (status === 403) return MESSAGES.forbidden;
  if (status === 404) return MESSAGES.notFound;
  return MESSAGES.unavailable;
};

const isTerminalError = (status) => status === 403 || status === 404;

const ContactRow = ({ contact, receivedAt, now }) => (
  <div className="registration-contact box">
    <div className="registration-contact-header">
      <span className="registration-endpoint-id">{contact.endpointId}</span>
      {contact.webrtc && <span className="tag is-info is-light">WebRTC</span>}
    </div>
    <div className="registration-field">
      <span className="registration-label">IP de origem:</span>
      {contact.sourceIp === null ? (
        <span className="registration-value">
          {MESSAGES.unknownSource}
          <span className="registration-hint">{MESSAGES.unknownSourceHint}</span>
        </span>
      ) : (
        <span className="registration-value">{formatAddress(contact.sourceIp, contact.sourcePort)}</span>
      )}
    </div>
    {contact.declaredIp !== null && (
      <div className="registration-field">
        <span className="registration-label">IP declarado:</span>
        <span className="registration-value">{formatAddress(contact.declaredIp, contact.declaredPort)}</span>
      </div>
    )}
    <div className="registration-field">
      <span className="registration-label">UserAgent:</span>
      <span className="registration-value">{contact.userAgent === null ? MESSAGES.noUserAgent : contact.userAgent}</span>
    </div>
    <div className="registration-field">
      <span className="registration-label">Expira em:</span>
      <span className="registration-value">{formatExpiry(contact, receivedAt, now)}</span>
    </div>
  </div>
);

ContactRow.propTypes = {
  contact: PropTypes.shape({
    endpointId: PropTypes.string.isRequired,
    webrtc: PropTypes.bool.isRequired,
    sourceIp: PropTypes.string,
    sourcePort: PropTypes.number,
    declaredIp: PropTypes.string,
    declaredPort: PropTypes.number,
    userAgent: PropTypes.string,
    expiresAt: PropTypes.string.isRequired,
    expiresInSeconds: PropTypes.number.isRequired,
  }).isRequired,
  receivedAt: PropTypes.number.isRequired,
  now: PropTypes.number.isRequired,
};

const EndpointRegistrationModal = ({ endpoint, onClose }) => {
  const { ipEndpoints } = useContext(PbxContext);
  const [registration, setRegistration] = useState(null);
  const [receivedAt, setReceivedAt] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [stale, setStale] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const activeRef = useRef(true);
  const requestSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const pollTimerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const mountedIpEndpointsRef = useRef(ipEndpoints);
  const fetchRef = useRef(null);

  const applyResult = useCallback((result) => {
    if (result.success) {
      const receivedNow = Date.now();
      setRegistration(result.data);
      setReceivedAt(receivedNow);
      setNow(receivedNow);
      setUpdatedAt(new Date(receivedNow));
      setErrorMessage('');
      setStale(false);
    } else if (isTerminalError(result.status)) {
      setRegistration(null);
      setErrorMessage(errorMessageFor(result.status));
      setStale(false);
    } else {
      setErrorMessage(MESSAGES.unavailable);
      setStale(true);
    }
    setLoading(false);
  }, []);

  const fetchRegistrations = useCallback(async () => {
    if (!activeRef.current) return;
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    inFlightRef.current = true;
    clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(() => fetchRef.current(), REGISTRATION_POLL_INTERVAL_MS);
    requestSeqRef.current += 1;
    const seq = requestSeqRef.current;
    const result = await fetchEndpointRegistrations(endpoint);
    inFlightRef.current = false;
    if (!activeRef.current) return;
    if (seq > appliedSeqRef.current) {
      appliedSeqRef.current = seq;
      applyResult(result || { success: false });
    }
    if (pendingRef.current) {
      pendingRef.current = false;
      fetchRef.current();
    }
  }, [endpoint, applyResult]);

  fetchRef.current = fetchRegistrations;

  useEffect(() => {
    activeRef.current = true;
    fetchRef.current();
    const tick = setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS);
    return () => {
      activeRef.current = false;
      clearInterval(tick);
      clearTimeout(pollTimerRef.current);
      clearTimeout(debounceTimerRef.current);
      pollTimerRef.current = null;
      debounceTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (ipEndpoints === mountedIpEndpointsRef.current) return;
    if (!ipEndpoints || ipEndpoints.endpoint !== endpoint) return;
    if (debounceTimerRef.current) return;
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      fetchRef.current();
    }, REGISTRATION_EVENT_DEBOUNCE_MS);
  }, [ipEndpoints, endpoint]);

  const contacts = registration ? registration.contacts : [];
  const title = registration && contacts.length > 0
    ? `Ramal ${endpoint} — ${contacts.length} registro(s)`
    : `Ramal ${endpoint}`;

  return ReactDOM.createPortal(
    <div className="modal is-active endpoint-registration-modal" role="dialog" aria-label={`Registro do ramal ${endpoint}`}>
      <div className="modal-background" onClick={onClose} />
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">{title}</p>
          <button type="button" className="delete" aria-label="Fechar" onClick={onClose} />
        </header>
        <section className="modal-card-body">
          {loading && <p className="registration-loading">Consultando registro...</p>}
          {!loading && errorMessage && (
            <div className="notification is-warning is-light registration-error">
              {errorMessage}
              {stale && <span className="registration-stale"> (dados desatualizados)</span>}
            </div>
          )}
          {!loading && registration && contacts.length === 0 && (
            <p className="registration-empty">{MESSAGES.noRegistration}</p>
          )}
          {!loading && registration && contacts.map((contact, index) => (
            <ContactRow
              key={`${contact.endpointId}-${contact.expiresAt}-${index}`}
              contact={contact}
              receivedAt={receivedAt}
              now={now}
            />
          ))}
        </section>
        <footer className="modal-card-foot registration-footer">
          {registration && registration.truncated && <span className="registration-truncated">{MESSAGES.truncated}</span>}
          {updatedAt && (
            <span className="registration-updated">
              Atualizado às {formatClock(updatedAt)}
              {stale && ' — desatualizado'}
            </span>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
};

EndpointRegistrationModal.propTypes = {
  endpoint: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EndpointRegistrationModal;
